'use client';

import { ScrollView } from 'tamagui';
import { motion } from 'framer-motion';
import { useLandingPageTemplate } from '../../hooks';
import {
  WorldMapSection,
  GlobeSection,
  CoverSection,
  TestimonialsSection,
  BentoSection,
  ScrollAnimationSection,
  InfiniteCardsSection,
  GlowingGridSection,
} from '../../components/sections';
import './home.css';

export const Home = () => {
  const { html, loading, error } = useLandingPageTemplate();

  const handleGetStarted = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/signup';
    }
  };

  // If custom template is loaded, render it as HTML
  if (html && !error) {
    return (
      <div
        dangerouslySetInnerHTML={{ __html: html }}
        style={{ width: '100%' }}
      />
    );
  }

  // If loading, show a loading state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  // If error, log it but continue with default
  if (error) {
    console.warn('[Home] Landing page template error:', error);
  }

  // Default component (used when no template is configured or on error)
  return (
    <ScrollView flex={1}>
      {/* Hero Section */}
      <div className="relative mx-auto flex w-full flex-col items-center justify-center overflow-hidden">
        {/* Decorative Borders */}
        <div className="absolute inset-y-0 left-0 h-full w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent dark:via-slate-800">
          <div className="absolute top-0 h-40 w-px bg-gradient-to-b from-transparent via-blue-500 to-transparent opacity-50" />
        </div>
        <div className="absolute inset-y-0 right-0 h-full w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent dark:via-slate-800">
          <div className="absolute top-0 h-40 w-px bg-gradient-to-b from-transparent via-blue-500 to-transparent opacity-50" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent dark:via-slate-800">
          <div className="absolute bottom-0 left-1/2 h-px w-40 -translate-x-1/2 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-50" />
        </div>

        {/* Background Gradient Blobs */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full bg-gradient-to-br from-blue-300/20 via-transparent to-transparent blur-3xl dark:from-blue-500/10" />
          <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-gradient-to-tr from-blue-300/20 via-transparent to-transparent blur-3xl dark:from-blue-500/10" />
          <div className="absolute top-1/2 left-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-slate-300/30 via-transparent to-transparent blur-3xl dark:from-slate-600/10" />
        </div>

        {/* Content */}
        <div className="relative z-10 px-4 py-20 md:py-32">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 flex justify-center"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50/50 px-4 py-2 backdrop-blur-sm dark:border-blue-900/30 dark:bg-blue-900/20">
              <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                ✨ Build Something Amazing
              </span>
            </div>
          </motion.div>

          {/* Hero Title with Word Animation */}
          <motion.h1 className="relative z-10 mx-auto max-w-5xl text-center text-4xl font-bold text-slate-900 md:text-5xl lg:text-7xl dark:text-slate-100">
            {['Launch', 'Your', 'App', 'in', 'Hours,', 'Not', 'Days'].map((word, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, filter: 'blur(10px)', y: 20 }}
                animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.1,
                  ease: 'easeOut',
                }}
                className="mr-2 inline-block"
              >
                {word === 'Hours,' ? (
                  <span className="bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 bg-clip-text text-transparent dark:from-blue-400 dark:via-blue-500 dark:to-blue-600">
                    {word}
                  </span>
                ) : word === 'Days' ? (
                  <span className="bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 bg-clip-text text-transparent dark:from-blue-400 dark:via-blue-500 dark:to-blue-600">
                    {word}
                  </span>
                ) : (
                  word
                )}
              </motion.span>
            ))}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="relative z-10 mx-auto max-w-2xl py-6 text-center text-lg font-normal text-slate-600 dark:text-slate-400"
          >
            A premium monorepo starter with Web, Mobile, and Serverless. Built with React, Tamagui, and TypeScript for teams that demand both speed and quality.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1 }}
            className="relative z-10 mt-8 flex flex-wrap items-center justify-center gap-4"
          >
            <button
              onClick={handleGetStarted}
              className="group relative flex items-center gap-2 overflow-hidden rounded-lg bg-blue-600 px-8 py-3 font-semibold text-white transition-all duration-300 hover:-translate-y-1 hover:bg-blue-700 hover:shadow-xl dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              <span className="relative z-10">Get Started Free</span>
              <svg
                className="relative z-10 h-5 w-5 transition-transform group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </button>
            <a
              href="#features"
              className="inline-flex items-center gap-2 rounded-lg border-2 border-slate-300 bg-white px-8 py-3 font-semibold text-slate-900 transition-all duration-300 hover:-translate-y-1 hover:border-blue-500 hover:shadow-lg dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:hover:border-blue-400"
            >
              Explore Features
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.2 }}
            className="relative z-10 mt-16 flex flex-wrap items-center justify-center gap-8 border-t border-slate-200 pt-8 dark:border-slate-700"
          >
            {stats.map((stat, index) => (
              <div key={index} className="flex flex-col items-center gap-1">
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stat.number}</span>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">{stat.label}</span>
              </div>
            ))}
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="relative z-10 mt-20 w-full max-w-4xl"
          >
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
              <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800">
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 via-transparent to-purple-500/20" />
                <img
                  src="https://images.unsplash.com/photo-1667372393119-3d4edf1acaeb?w=1200&h=675&fit=crop"
                  alt="Dashboard Preview"
                  className="h-full w-full object-cover opacity-90 mix-blend-overlay"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <button className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-all hover:bg-white/30">
                    <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <section id="features" className="relative w-full bg-white px-4 py-20 dark:bg-slate-950 md:py-32">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold text-slate-900 dark:text-white md:text-5xl">
              Powerful Features
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-slate-600 dark:text-slate-400">
              Everything you need to build, scale, and deploy modern applications
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featureList.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative"
              >
                <div className="absolute -inset-0.5 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 blur-xl transition-all duration-500 group-hover:opacity-20" />
                <div className="relative rounded-xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-2 hover:border-blue-500 hover:shadow-xl dark:border-slate-700 dark:bg-slate-800 dark:hover:border-blue-500">
                  <div className="mb-4 text-4xl">{feature.icon}</div>
                  <h3 className="mb-3 text-xl font-bold text-slate-900 dark:text-white">{feature.title}</h3>
                  <p className="mb-4 text-slate-600 dark:text-slate-400">{feature.description}</p>
                  <div className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 transition-all dark:text-blue-400">
                    Learn more
                    <svg className="h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section className="relative w-full bg-gradient-to-b from-slate-50 to-white px-4 py-20 dark:from-slate-900 dark:to-slate-950 md:py-32">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold text-slate-900 dark:text-white md:text-5xl">
              Built with Modern Tech
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-slate-600 dark:text-slate-400">
              Industry-leading technologies that power modern development
            </p>
          </motion.div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
            {techStack.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="group relative"
              >
                <div className="rounded-xl border border-slate-200 bg-white p-4 text-center transition-all duration-300 hover:-translate-y-1 hover:border-blue-500 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:hover:border-blue-500">
                  <div className="mb-2 text-3xl">{tech.icon}</div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">{tech.name}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Aceternity Sections */}
      <CoverSection />
      <BentoSection />
      <WorldMapSection />
      <GlobeSection />
      <TestimonialsSection />
      <ScrollAnimationSection />
      <InfiniteCardsSection />
      <GlowingGridSection />

      {/* Final CTA Section */}
      <section className="relative w-full overflow-hidden bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-20 dark:from-blue-900 dark:to-blue-950 md:py-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,255,255,0.1)_0%,transparent_50%),radial-gradient(circle_at_80%_80%,rgba(255,255,255,0.1)_0%,transparent_50%)]" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative z-10 mx-auto max-w-3xl text-center"
        >
          <h2 className="mb-6 text-4xl font-bold text-white md:text-5xl">Ready to Launch?</h2>
          <p className="mb-10 text-lg text-blue-100">
            Join hundreds of developers building next-generation apps with TaskMaster
          </p>
          <button
            onClick={handleGetStarted}
            className="group relative inline-flex items-center gap-2 overflow-hidden rounded-lg bg-white px-10 py-4 font-semibold text-blue-600 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl dark:bg-slate-900 dark:text-blue-400"
          >
            <span className="relative z-10">Start Building Now</span>
            <svg
              className="relative z-10 h-5 w-5 transition-transform group-hover:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </button>
        </motion.div>
      </section>
    </ScrollView>
  );
};

interface Feature {
  icon: string;
  title: string;
  description: string;
}

const stats = [
  { number: '100%', label: 'TypeScript' },
  { number: '3', label: 'Platforms' },
  { number: '⚡', label: 'Blazing Fast' },
];

const featureList: Feature[] = [
  {
    icon: '⚡',
    title: 'Lightning Fast',
    description: 'Built with Vite for sub-second HMR and blazing-fast production builds.',
  },
  {
    icon: '📱',
    title: 'Cross-Platform',
    description: 'Share code between Web, iOS, Android, and Worker serverless functions.',
  },
  {
    icon: '🔒',
    title: 'Secure Auth',
    description: 'Enterprise-grade authentication with Supabase integration out of the box.',
  },
  {
    icon: '🎨',
    title: 'Beautiful UI',
    description: 'Tamagui components with Aceternity design patterns for stunning interfaces.',
  },
  {
    icon: '📦',
    title: 'Monorepo Setup',
    description: 'Organized workspace with shared components, utilities, and configurations.',
  },
  {
    icon: '🚀',
    title: 'Deploy Anywhere',
    description: 'Ready to deploy to Cloudflare Workers, Vercel, or any hosting provider.',
  },
];

const techStack = [
  { icon: '⚛️', name: 'React' },
  { icon: '📱', name: 'React Native' },
  { icon: '🎨', name: 'Tamagui' },
  { icon: '⚡', name: 'Vite' },
  { icon: '🔐', name: 'Supabase' },
  { icon: '☁️', name: 'Cloudflare' },
  { icon: '🔧', name: 'TypeScript' },
  { icon: '📦', name: 'Bun' },
];
